import React from 'react';
import { getBusinessUnitLabel } from '@/types/common';
import {
  formatHandoverSchedule,
  HandoverIdField,
  pendingScheduleLeg,
  SCHEDULE_LEG_COPY,
} from '@/components';
import dayjs from 'dayjs';
import type { AddHandoverModalInitialData } from '../AddHandoverModal';

/** One stored per-part checklist entry (from handover_checklist). */
interface ChecklistPhotoItem {
  code: string;
  label: string;
  category: 'EQUIPMENT' | 'CONDITION';
  present: boolean;
  photoUrl: string | null;
}

/** Per-part checklist with a photo per present item. Replaces the old single
 * vehicle photo + flat equipment list once a handover has structured data. */
const PartChecklistGrid: React.FC<{ items: ChecklistPhotoItem[] }> = ({
  items,
}) => (
  <div className="grid grid-cols-2 gap-3 mt-2 mb-2">
    {items.map((item) => (
      <div
        key={item.code}
        className="rounded-lg border border-gray-100 overflow-hidden"
      >
        {item.present && item.photoUrl ? (
          <a
            href={item.photoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block h-40 bg-gray-50"
          >
            <img
              src={item.photoUrl}
              alt={item.label}
              className="w-full h-40 object-contain"
            />
          </a>
        ) : (
          <div
            className={`w-full h-40 flex items-center justify-center text-xs font-medium ${
              item.present
                ? 'bg-amber-50 text-amber-600'
                : 'bg-gray-50 text-gray-400'
            }`}
          >
            {item.present ? 'Ada — tanpa foto' : 'Tidak ada'}
          </div>
        )}
        <div className="px-2 py-1.5 flex items-center justify-between gap-1">
          <span className="text-[12px] text-gray-700 truncate">
            {item.label}
          </span>
          <span
            className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${
              item.present ? 'bg-green-500' : 'bg-gray-300'
            }`}
          />
        </div>
      </div>
    ))}
  </div>
);

interface FormData {
  vehicleHubID?: string;
  vehicleHubName?: string;
  vehicleBusinessUnit?: string;
  vehicleID: string;
  driverID: number;
  driverName: string;
  driverCode: string;
  clientID: number;
  clientName: string;
  hubID: number;
  hubName: string;
  handoverDate: string;
  bastLink: string;
  odometer?: string;
  notes: string;
  depositMitraToDash?: string;
  vehicleHandoverImageURL: string;
  unitEquipment: string[];
  sellingPrice?: number;
}

interface HandoverDetailViewProps {
  detailTab: 'details' | 'equipment';
  formData: FormData;
  initialData?: AddHandoverModalInitialData | null;
  vehicleEquipment: string[];
  onTabChange: (tab: 'details' | 'equipment') => void;
  /**
   * Opens the ops date override. Rendered only while a date is actually pending
   * — on any other rung the endpoint refuses, so the link would be a dead end.
   */
  onEditSchedule?: () => void;
}

// Utility component for field display (Migrated from EditIssueModal)
const FieldDisplay: React.FC<{
  label: string;
  value: string | React.ReactNode;
  className?: string;
  hasBorder?: boolean;
  labelClassName?: string;
}> = ({
  label,
  value,
  className = '',
  hasBorder = true,
  labelClassName = '',
}) => (
  <div className={`pb-2 ${className}`}>
    <label className={`block mb-1 ${labelClassName || 'heading-field'}`}>
      {label}
    </label>
    <div
      className={`text-sm font-medium text-gray-900 break-words whitespace-pre-wrap pb-3 ${
        hasBorder ? 'border-b border-gray-200' : ''
      }`}
    >
      {value || '-'}
    </div>
  </div>
);

// Section Header (Migrated from EditIssueModal)
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className="bg-gray-50 px-6 py-1 mb-4">
    <span className="font-medium rounded-t-lg heading-field tracking-wider">
      {title}
    </span>
  </div>
);

// Info Section (Adapted from EditIssueModal for Handover data)
const InfoDisplay: React.FC<{ data: any }> = ({ data }) => (
  <>
    <SectionHeader title="Info" />
    <div className="p-6 pt-0">
      <div className="grid grid-cols-2 gap-4">
        <FieldDisplay
          label="Created By"
          value={data?.createdBy || '-'}
          className="mb-0"
          hasBorder={false}
        />
        <FieldDisplay
          label="Created Date"
          value={
            data?.createdAt
              ? dayjs(data.createdAt).format('MMMM D, YYYY [at] HH.mm')
              : '-'
          }
          className="mb-0"
          hasBorder={false}
        />
      </div>
      <div className="border-t border-gray-200 mt-2 mb-4" />
      <div className="grid grid-cols-2 gap-4">
        <FieldDisplay
          label="Last Updated By"
          value={data?.updatedBy || '-'}
          hasBorder={false}
        />
        <FieldDisplay
          label="Last Updated"
          value={
            data?.updatedAt
              ? dayjs(data.updatedAt).format('MMMM D, YYYY [at] HH.mm')
              : '-'
          }
          hasBorder={false}
        />
      </div>
    </div>
  </>
);

const HandoverDetailView: React.FC<HandoverDetailViewProps> = ({
  detailTab,
  formData,
  initialData,
  vehicleEquipment,
  onTabChange,
  onEditSchedule,
}) => {
  const leg = pendingScheduleLeg(initialData?.status);
  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 flex-shrink-0 sticky top-0 bg-white z-10">
        <nav className="flex space-x-8 px-6">
          <button
            onClick={() => onTabChange('details')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              detailTab === 'details'
                ? 'border-purple-600 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Handover Details
          </button>
          <button
            onClick={() => onTabChange('equipment')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              detailTab === 'equipment'
                ? 'border-purple-600 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Equipment Checklist
          </button>
        </nav>
      </div>

      {/* Detail View Content */}
      <div className="flex-1">
        {detailTab === 'details' ? (
          <div>
            {/* Top Section */}
            <div className="px-6">
              <div className="pt-6 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Plate Number</p>
                    <p className="text-sm font-medium text-black">
                      {(initialData as any)?.platNomor || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Harga Jual (Harian)</p>
                    <p className="text-sm font-medium text-black">
                      {formData.sellingPrice
                        ? `Rp. ${formData.sellingPrice.toLocaleString('id-ID')}`
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-4 pb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">OEM Name</p>
                    <p className="text-sm font-medium text-black">
                      {(initialData as any)?.oemName || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Model Name</p>
                    <p className="text-sm font-medium text-black">
                      {(initialData as any)?.modelName || '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Hub Location & Business Unit */}
              <div className="pt-4 pb-4 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Hub Location</p>
                    <p className="text-sm font-medium text-black break-words">
                      {formData.vehicleHubName ||
                        (initialData as any)?.vehicleHubName ||
                        '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Business Unit</p>
                    <p className="text-sm font-medium text-black break-words">
                      {getBusinessUnitLabel(
                        formData.vehicleBusinessUnit ||
                          (initialData as any)?.vehicleBusinessUnit
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Handover Detail Header */}
            <div className="bg-gray-50 px-6 py-2 font-medium heading-field">
              Handover Detail
            </div>

            {/* Handover Detail Content */}
            <div className="px-6">
              {/* The id ops quote in tickets and paste into queries. It was a
                  list column until it proved unreadable at that width; here it
                  has room, and the copy button is what it is actually for. */}
              <div className="pt-4 pb-4 border-b border-gray-200">
                <HandoverIdField
                  id={(initialData as any)?.ID}
                  labelClassName="heading-field mb-1"
                />
              </div>

              {/* Clients & Pitstop */}
              <div className="pt-4 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Clients</p>
                    <p className="text-sm font-medium text-black break-words">
                      {formData.clientName ||
                        (initialData as any)?.clientName ||
                        '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Pitstop</p>
                    <p className="text-sm font-medium text-black break-words">
                      {formData.hubName || (initialData as any)?.hubName || '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Mitra Code & Mitra Name */}
              <div className="pt-4 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Mitra Code</p>
                    <p className="text-sm font-medium text-black break-words">
                      {formData.driverCode ||
                        (initialData as any)?.driverCode ||
                        '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Mitra Name</p>
                    <p className="text-sm font-medium text-black break-words">
                      {formData.driverName ||
                        (initialData as any)?.driverName ||
                        '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Jadwal — the PLAN, above the actual pickup below it. On a unit
                  still waiting to be collected or waiting to come back these are
                  the only dates the row has; the actual ones are still null. */}
              <div className="pt-4 pb-4 border-b border-gray-200">
                {/* Ops override, offered from the detail exactly when there is a
                    date left to move. */}
                {leg && onEditSchedule && (
                  <div className="flex justify-end -mt-1 mb-1">
                    <button
                      type="button"
                      onClick={onEditSchedule}
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary underline hover:opacity-80"
                    >
                      <i className="ri-calendar-event-line text-[14px]" />
                      {SCHEDULE_LEG_COPY[leg].action}
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Jadwal Handover</p>
                    <p className="text-sm font-medium text-black">
                      {formatHandoverSchedule(
                        (initialData as any)?.scheduledHandoverDate,
                        {
                          startTime: (initialData as any)?.slotStartTime,
                          endTime: (initialData as any)?.slotEndTime,
                        }
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Jadwal Pengembalian</p>
                    <p className="text-sm font-medium text-black">
                      {/* Date only — the slot window is the pickup's. */}
                      {formatHandoverSchedule(
                        (initialData as any)?.scheduledReturnDate
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tanggal Handover */}
              <div className="pt-4 pb-4 border-b border-gray-200">
                <p className="heading-field mb-1">Tanggal Handover</p>
                <p className="text-sm font-medium text-black">
                  {formData.handoverDate
                    ? dayjs(formData.handoverDate).format('DD/MM/YYYY, HH:mm')
                    : '-'}
                </p>
              </div>

              {/* BAST Document */}
              <div className="pt-4 pb-4 border-b border-gray-200">
                <p className="heading-field mb-1">Document BAST</p>
                {(() => {
                  const bastUrl =
                    formData.bastLink || (initialData as any)?.bastLink || '';
                  if (!bastUrl) {
                    return <p className="text-sm font-medium text-black">-</p>;
                  }
                  return (
                    <a
                      href={bastUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline break-all"
                    >
                      <i className="ri-file-pdf-2-line text-base" />
                      View document
                    </a>
                  );
                })()}
              </div>

              {/* Deposit */}
              <div className="pt-4 pb-4 border-b border-gray-200">
                <p className="heading-field mb-1">Deposit Mitra to Dash</p>
                <p className="text-sm font-medium text-black">
                  {(() => {
                    const depositValue =
                      formData.depositMitraToDash ||
                      (initialData as any)?.depositMitraToDash;
                    if (!depositValue) return '-';
                    return `Rp. ${Number(depositValue).toLocaleString('id-ID')}`;
                  })()}
                </p>
              </div>

              {/* Notes */}
              <div className="pt-4 pb-4">
                <p className="heading-field mb-1">Notes</p>
                <p className="text-sm font-medium text-black break-words whitespace-pre-wrap">
                  {formData.notes || (initialData as any)?.notes || '-'}
                </p>
              </div>
            </div>

            {/* Info Section (Repo Style) */}
            <div className="bg-gray-50 px-6 py-2 font-medium heading-field">
              Info
            </div>
            <div className="px-6">
              {/* Created By + Created Date */}
              <div className="pt-4 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Created By</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.createdBy || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Created Date</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.createdAt
                        ? dayjs((initialData as any).createdAt).format(
                            'DD/MM/YYYY [at] HH:mm'
                          )
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Last Updated By + Last Updated */}
              <div className="pt-4 pb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="heading-field mb-1">Last Updated By</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.updatedBy || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="heading-field mb-1">Last Updated</p>
                    <p className="text-sm font-medium text-black break-words">
                      {(initialData as any)?.updatedAt
                        ? dayjs((initialData as any).updatedAt).format(
                            'DD/MM/YYYY [at] HH:mm'
                          )
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="p-6 pb-0">
              <FieldDisplay
                label="Odometer at Handover"
                value={formData.odometer + ' KM'}
                hasBorder={true}
              />
              {(() => {
                const checklist = ((initialData as any)?.handoverChecklist ??
                  []) as ChecklistPhotoItem[];
                // Per-part handovers: one photo per item. Legacy handovers fall
                // back to the single vehicle photo + flat equipment list.
                if (checklist.length > 0) {
                  return (
                    <FieldDisplay
                      label="Kelengkapan & Kondisi Unit"
                      value={<PartChecklistGrid items={checklist} />}
                      hasBorder={true}
                    />
                  );
                }
                return (
                  <>
                    <FieldDisplay
                      label="Foto Kendaraan"
                      value={
                        formData.vehicleHandoverImageURL ||
                        (initialData as any)?.vehicleHandoverImageURL ? (
                          <div className="rounded-lg overflow-hidden mt-2 mb-2 border border-gray-100">
                            <img
                              src={
                                formData.vehicleHandoverImageURL ||
                                (initialData as any)?.vehicleHandoverImageURL
                              }
                              alt="Vehicle"
                              className="w-full h-40 object-cover bg-gray-100"
                            />
                          </div>
                        ) : (
                          <div className="border-2 border-gray-200 rounded-lg mt-2 mb-2">
                            <div className="p-8 flex flex-col justify-center items-center text-center">
                              <div className="text-gray-300 text-4xl mb-4">
                                <svg
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  className="w-12 h-12 mx-auto"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                              <p className="text-gray-500 font-medium">
                                No image uploaded
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                Image was not provided during handover
                              </p>
                            </div>
                          </div>
                        )
                      }
                      hasBorder={true}
                    />

                    <FieldDisplay
                      className="mt-2"
                      label="Kelengkapan Unit"
                      value={
                        vehicleEquipment.length > 0 ? (
                          <ul className="list-disc list-inside space-y-1 mt-1">
                            {vehicleEquipment.map((equipment) => (
                              <li
                                key={equipment}
                                className="text-sm text-gray-700"
                              >
                                {equipment}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-500">no equipment</p>
                        )
                      }
                      hasBorder={true}
                    />
                  </>
                );
              })()}

              {/* Return checklist (per-part photos) once the unit is returned. */}
              {(() => {
                const returnChecklist = ((initialData as any)
                  ?.returnChecklist ?? []) as ChecklistPhotoItem[];
                const returnCondition = (initialData as any)
                  ?.returnCondition as string | undefined;
                if (returnChecklist.length === 0 && !returnCondition) {
                  return null;
                }
                return (
                  <>
                    {returnCondition && (
                      <FieldDisplay
                        className="mt-2"
                        label="Kondisi Pengembalian"
                        value={returnCondition}
                        hasBorder={true}
                      />
                    )}
                    {returnChecklist.length > 0 && (
                      <FieldDisplay
                        className="mt-2"
                        label="Kelengkapan & Kondisi (Pengembalian)"
                        value={<PartChecklistGrid items={returnChecklist} />}
                        hasBorder={true}
                      />
                    )}
                  </>
                );
              })()}

              <FieldDisplay
                className="mt-2"
                label="Notes"
                value={formData.notes || (initialData as any)?.notes}
                hasBorder={false}
              />
            </div>

            <InfoDisplay data={initialData} />
          </div>
        )}
      </div>
    </div>
  );
};

export default HandoverDetailView;
